/**
 * Customer/player inactivity auto-logout for Alfa9 (WS0003).
 *
 * Uses the existing player session key and POST /auth/logout.
 * Does not replace login, refresh, or token format.
 * Last activity is stored outside the session JSON so token refresh cannot reset the timer.
 */

import { PLAYER_PUBLIC_SITE_ID } from './playerLoading.js';

export const PLAYER_INACTIVITY_MS = 10 * 60 * 1000;
export const PLAYER_SESSION_KEY = 'ninet6_player_session';
export const PLAYER_ACTIVITY_KEY = 'ninet6_player_last_activity';
export const PLAYER_IDLE_LOGOUT_KEY = 'ninet6_player_idle_logout';
export const PLAYER_INACTIVITY_LOGIN_PATH = '/';
export const PLAYER_ACTIVITY_THROTTLE_MS = 1000;
export const PLAYER_INACTIVITY_CHECK_MS = 1000;

export const PLAYER_ACTIVITY_EVENTS = [
  'pointerdown',
  'pointerup',
  'pointermove',
  'mousedown',
  'mouseup',
  'mousemove',
  'click',
  'keydown',
  'keypress',
  'keyup',
  'touchstart',
  'touchend',
  'touchmove',
  'wheel',
  'scroll',
  'input',
];

const FETCH_WRAPPED = '__ninet6PlayerInactivityFetch';
let watchHandle = null;

export function playerAuthApiBase(siteId = PLAYER_PUBLIC_SITE_ID) {
  return `/api/site/${encodeURIComponent(siteId)}/v1`;
}

export function playerAuthLogoutUrl(siteId = PLAYER_PUBLIC_SITE_ID) {
  return `${playerAuthApiBase(siteId)}/auth/logout`;
}

export function readPlayerSession(storage) {
  try {
    const raw = storage?.getItem?.(PLAYER_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasPlayerSession(session) {
  if (!session || typeof session !== 'object') return false;
  const access = String(session.accessToken || session.token || '').trim();
  const refresh = String(session.refreshToken || '').trim();
  return Boolean(access || refresh);
}

export function readPlayerLastActivityAt(storage) {
  try {
    const raw = storage?.getItem?.(PLAYER_ACTIVITY_KEY);
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

export function writePlayerLastActivityAt(storage, at) {
  const value = Number(at);
  if (!Number.isFinite(value) || value <= 0) return 0;
  storage.setItem(PLAYER_ACTIVITY_KEY, String(Math.floor(value)));
  return Math.floor(value);
}

export function clearPlayerInactivityStorage(storage, { keepIdleFlag = false } = {}) {
  try {
    storage.removeItem(PLAYER_SESSION_KEY);
  } catch {
    /* ignore */
  }
  try {
    storage.removeItem(PLAYER_ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
  if (!keepIdleFlag) {
    try {
      storage.removeItem(PLAYER_IDLE_LOGOUT_KEY);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Missing/zero last_activity is not idle-expired. Hard Refresh must not
 * logout a still-valid JWT just because the activity key was never stamped.
 * A real timestamp that is already >= timeout still expires.
 */
export function isPlayerIdleExpired({
  session,
  lastActivityAt,
  now,
  timeoutMs = PLAYER_INACTIVITY_MS,
} = {}) {
  if (!hasPlayerSession(session)) return false;
  const at = Number(lastActivityAt) || 0;
  if (at <= 0) return false;
  return Number(now) - at >= timeoutMs;
}

/** Stamp now only when a live session has no usable activity timestamp. Never overwrite a valid stamp. */
export function ensurePlayerLastActivityAt(storage, at) {
  const session = readPlayerSession(storage);
  if (!hasPlayerSession(session)) return 0;
  const existing = readPlayerLastActivityAt(storage);
  if (existing > 0) return existing;
  const value = Number(at);
  const stamp = Number.isFinite(value) && value > 0 ? Math.floor(value) : Date.now();
  return writePlayerLastActivityAt(storage, stamp);
}

export function shouldStampActivityOnSessionWrite(previousSession, nextSession) {
  return !hasPlayerSession(previousSession) && hasPlayerSession(nextSession);
}

export function isExemptPlayerAuthUrl(url) {
  const value = String(url || '');
  if (!value) return true;
  if (/\/public\//.test(value)) return true;
  if (/\/auth\/login(?:\?|$)/.test(value)) return true;
  if (/\/auth\/register(?:\?|$)/.test(value)) return true;
  if (/\/auth\/logout(?:\?|$)/.test(value)) return true;
  if (/\/auth\/username-available/.test(value)) return true;
  if (/\/auth\/phone-available/.test(value)) return true;
  return false;
}

export function isPlayerSiteApiUrl(url, siteId = PLAYER_PUBLIC_SITE_ID) {
  const value = String(url || '');
  return value.includes(`/api/site/${siteId}/v1`);
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input === 'object' && input.url) return String(input.url);
  return '';
}

function currentPathname(locationLike) {
  try {
    return String(locationLike?.pathname || '');
  } catch {
    return '';
  }
}

export function createPlayerInactivityController(deps = {}) {
  const storage = deps.storage;
  const now = deps.now || (() => Date.now());
  const fetchImpl = deps.fetch;
  const locationLike = deps.location;
  const addEventListener = deps.addEventListener;
  const removeEventListener = deps.removeEventListener;
  const setIntervalFn = deps.setInterval;
  const clearIntervalFn = deps.clearInterval;
  const siteId = deps.siteId || PLAYER_PUBLIC_SITE_ID;
  const timeoutMs = Number(deps.timeoutMs) > 0 ? Number(deps.timeoutMs) : PLAYER_INACTIVITY_MS;
  const checkEveryMs = Number(deps.checkEveryMs) > 0 ? Number(deps.checkEveryMs) : PLAYER_INACTIVITY_CHECK_MS;
  const throttleMs = Number(deps.throttleMs) >= 0 ? Number(deps.throttleMs) : PLAYER_ACTIVITY_THROTTLE_MS;
  const eventTarget = deps.eventTarget;
  const documentLike = deps.document;

  let started = false;
  let loggingOut = false;
  let redirected = false;
  let lastStampAt = 0;
  let timerId = 0;
  const listeners = [];
  const redirects = [];
  const logoutCalls = [];
  let rawFetch = fetchImpl;

  function session() {
    return readPlayerSession(storage);
  }

  function markIdleFlag() {
    try {
      storage.setItem(PLAYER_IDLE_LOGOUT_KEY, '1');
    } catch {
      /* ignore */
    }
  }

  function noteActivity(at = now()) {
    if (!hasPlayerSession(session())) return false;
    const ts = Number(at) || now();
    if (lastStampAt && ts - lastStampAt < throttleMs) {
      return false;
    }
    lastStampAt = ts;
    redirected = false;
    writePlayerLastActivityAt(storage, ts);
    try {
      storage.removeItem(PLAYER_IDLE_LOGOUT_KEY);
    } catch {
      /* ignore */
    }
    return true;
  }

  function goToLogin() {
    if (redirected) return;
    redirected = true;
    const path = currentPathname(locationLike);
    redirects.push(PLAYER_INACTIVITY_LOGIN_PATH);
    if (path === PLAYER_INACTIVITY_LOGIN_PATH) return;
    try {
      if (typeof locationLike?.replace === 'function') {
        locationLike.replace(PLAYER_INACTIVITY_LOGIN_PATH);
      }
    } catch {
      /* ignore */
    }
  }

  function logoutForInactivity() {
    if (loggingOut) return false;
    const current = session();
    if (!hasPlayerSession(current) && !storage.getItem?.(PLAYER_IDLE_LOGOUT_KEY)) {
      return false;
    }
    loggingOut = true;
    const refreshToken = String(current?.refreshToken || '').trim();
    const accessToken = String(current?.accessToken || current?.token || '').trim();
    markIdleFlag();
    clearPlayerInactivityStorage(storage, { keepIdleFlag: true });
    const url = playerAuthLogoutUrl(siteId);
    logoutCalls.push({ url, refreshToken: Boolean(refreshToken) });
    try {
      eventTarget?.dispatchEvent?.(new CustomEvent('ninet6-player-inactivity-logout'));
    } catch {
      /* ignore */
    }
    goToLogin();
    if (typeof rawFetch === 'function' && refreshToken) {
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      Promise.resolve(
        rawFetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ refreshToken }),
          credentials: 'same-origin',
        }),
      ).catch(() => {});
    }
    loggingOut = false;
    return true;
  }

  function resolveLastActivityAt(at = now()) {
    const stamped = ensurePlayerLastActivityAt(storage, at);
    if (stamped > 0) lastStampAt = stamped;
    return stamped;
  }

  function check(at = now()) {
    const idleFlag = storage.getItem?.(PLAYER_IDLE_LOGOUT_KEY) === '1';
    const current = session();
    if (!hasPlayerSession(current)) {
      // Boot, the 1-second check, and refresh all open Login when the expiry
      // marker is set. A guest has no marker. Refresh must not clear it.
      if (idleFlag) goToLogin();
      return { expired: Boolean(idleFlag), loggedIn: false };
    }
    const lastActivityAt = resolveLastActivityAt(at);
    const expired = isPlayerIdleExpired({
      session: current,
      lastActivityAt,
      now: at,
      timeoutMs,
    });
    if (expired) {
      logoutForInactivity();
      return { expired: true, loggedIn: false };
    }
    return { expired: false, loggedIn: true };
  }

  function onUserActivity() {
    if (documentLike && documentLike.visibilityState && documentLike.visibilityState !== 'visible') {
      check();
      return;
    }
    noteActivity();
  }

  function onVisibilityOrResume() {
    check();
  }

  function onStorage(ev) {
    const key = ev?.key;
    if (key && key !== PLAYER_SESSION_KEY && key !== PLAYER_ACTIVITY_KEY && key !== PLAYER_IDLE_LOGOUT_KEY) {
      return;
    }
    if (key === PLAYER_ACTIVITY_KEY && hasPlayerSession(session())) {
      lastStampAt = readPlayerLastActivityAt(storage);
      return;
    }
    check(now(), { enforceIdleFlag: true });
  }

  function onSessionWrite(previousRaw, nextRaw) {
    let previous = null;
    let next = null;
    try {
      previous = previousRaw ? JSON.parse(previousRaw) : null;
    } catch {
      previous = null;
    }
    try {
      next = nextRaw ? JSON.parse(nextRaw) : null;
    } catch {
      next = null;
    }
    if (shouldStampActivityOnSessionWrite(previous, next)) {
      lastStampAt = 0;
      noteActivity();
      return;
    }
    if (!hasPlayerSession(next)) {
      try {
        storage.removeItem(PLAYER_ACTIVITY_KEY);
      } catch {
        /* ignore */
      }
    }
  }

  function wrapStorage() {
    if (!storage || typeof storage.setItem !== 'function') return () => {};
    if (storage.__ninet6InactivityWrapped) return () => {};
    const originalSet = storage.setItem.bind(storage);
    const originalRemove = storage.removeItem.bind(storage);
    storage.setItem = (key, value) => {
      const prev = key === PLAYER_SESSION_KEY ? storage.getItem(PLAYER_SESSION_KEY) : null;
      originalSet(key, value);
      if (key === PLAYER_SESSION_KEY) onSessionWrite(prev, value);
    };
    storage.removeItem = (key) => {
      originalRemove(key);
      if (key === PLAYER_SESSION_KEY) {
        try {
          originalRemove(PLAYER_ACTIVITY_KEY);
        } catch {
          /* ignore */
        }
      }
    };
    storage.__ninet6InactivityWrapped = true;
    return () => {
      storage.setItem = originalSet;
      storage.removeItem = originalRemove;
      delete storage.__ninet6InactivityWrapped;
    };
  }

  function wrapFetch() {
    if (typeof fetchImpl !== 'function' || !deps.installFetch) return () => {};
    const target = deps.fetchOwner;
    if (!target || target[FETCH_WRAPPED]) return () => {};
    const original = typeof target.fetch === 'function' ? target.fetch.bind(target) : fetchImpl;
    rawFetch = original;
    const wrapped = (input, init) => {
      const url = requestUrl(input);
      if (isPlayerSiteApiUrl(url, siteId) && !isExemptPlayerAuthUrl(url)) {
        const ts = now();
        const expired = isPlayerIdleExpired({
          session: session(),
          lastActivityAt: resolveLastActivityAt(ts),
          now: ts,
          timeoutMs,
        });
        if (expired) {
          logoutForInactivity();
          return Promise.reject(Object.assign(new Error('Session expired. Please log in again.'), { status: 401 }));
        }
      }
      return original(input, init);
    };
    target.fetch = wrapped;
    target[FETCH_WRAPPED] = true;
    return () => {
      target.fetch = original;
      delete target[FETCH_WRAPPED];
    };
  }

  function listen(type, fn, options) {
    if (typeof addEventListener !== 'function') return;
    addEventListener(type, fn, options);
    listeners.push([type, fn, options]);
  }

  let unwrapStorage = () => {};
  let unwrapFetch = () => {};

  function start() {
    if (started) return api;
    started = true;
    unwrapStorage = wrapStorage();
    unwrapFetch = wrapFetch();
    check();
    PLAYER_ACTIVITY_EVENTS.forEach((type) => listen(type, onUserActivity, true));
    listen('visibilitychange', onVisibilityOrResume);
    listen('focus', onVisibilityOrResume);
    listen('pageshow', onVisibilityOrResume);
    listen('storage', onStorage);
    if (typeof setIntervalFn === 'function') {
      timerId = setIntervalFn(() => check(), checkEveryMs);
    }
    return api;
  }

  function stop() {
    if (!started) return;
    started = false;
    listeners.forEach(([type, fn, options]) => {
      try {
        removeEventListener?.(type, fn, options);
      } catch {
        /* ignore */
      }
    });
    listeners.length = 0;
    if (timerId && typeof clearIntervalFn === 'function') clearIntervalFn(timerId);
    timerId = 0;
    unwrapStorage();
    unwrapFetch();
    unwrapStorage = () => {};
    unwrapFetch = () => {};
  }

  const api = {
    start,
    stop,
    check,
    noteActivity,
    logoutForInactivity,
    redirects,
    logoutCalls,
    isStarted: () => started,
  };
  return api;
}

export function startPlayerInactivityWatch(options = {}) {
  if (typeof window === 'undefined') return null;
  if (watchHandle) return watchHandle;
  const owner = window;
  watchHandle = createPlayerInactivityController({
    storage: window.localStorage,
    now: () => Date.now(),
    fetch: typeof owner.fetch === 'function' ? owner.fetch.bind(owner) : undefined,
    fetchOwner: owner,
    installFetch: true,
    location: window.location,
    addEventListener: window.addEventListener.bind(window),
    removeEventListener: window.removeEventListener.bind(window),
    setInterval: window.setInterval.bind(window),
    clearInterval: window.clearInterval.bind(window),
    eventTarget: window,
    document: window.document,
    ...options,
  });
  watchHandle.start();
  return watchHandle;
}

export function stopPlayerInactivityWatch() {
  if (!watchHandle) return;
  watchHandle.stop();
  watchHandle = null;
}

export function __resetPlayerInactivityForTests() {
  stopPlayerInactivityWatch();
}
